<template>
  <div class="mb-6 overflow-auto">
    <table class="w-full table table-report">
      <thead class="p-3">
        <tr>
          <th v-if="selectable" class="px-4 py-2">
            <input
              type="checkbox"
              aria-label="Select accounts on this page"
              :checked="
                allSelected ||
                  (eligibleRows.length > 0 &&
                    eligibleRows.every(row => selectedIds.includes(row.id)))
              "
              :disabled="allSelected || actionsDisabled || !eligibleRows.length"
              @change="
                $emit('select', {
                  ids: eligibleRows.map(row => row.id),
                  checked: $event.target.checked
                })
              "
            />
          </th>
          <th
            v-for="(key, index) in columns"
            :key="index"
            :class="{ 'text-red active': sortKey == key }"
            class="cursor-pointer text-left px-4 py-2 capitalize"
          >
            <button
              type="button"
              :disabled="serverSide && key === 'teamHistory'"
              @click="sortBy(key)"
            >
              {{ columnLabels[key] || key }}
              <span class="arrow" :class="sortOrders[key] > 0 ? 'asc' : 'dsc'">
              </span>
            </button>
          </th>
          <th class="text-left px-4 py-2 capitalize">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(entry, index) in filteredData"
          :key="entry.id || index"
          class="hover:bg-gray-100 transition intro-x"
        >
          <td v-if="selectable" class="border-b px-4 py-2">
            <input
              type="checkbox"
              :aria-label="`Select ${entry.name}`"
              :checked="
                entry.active !== false &&
                  (allSelected || selectedIds.includes(entry.id))
              "
              :disabled="
                entry.active === false || allSelected || actionsDisabled
              "
              @change="
                $emit('select', {
                  ids: [entry.id],
                  checked: $event.target.checked
                })
              "
            />
          </td>
          <td v-for="(key, i) in columns" :key="i" class="border-b px-4 py-2">
            <span
              v-if="key !== 'active' && key !== 'role'"
              :class="{ 'font-bold text-base': key == 'name' }"
              class="block"
            >
              {{
                dateColumns.includes(key)
                  ? formatActivityDate(entry[key])
                  : entry[key]
              }}
              <span
                v-if="key === 'teamHistory' && entry.review"
                class="block text-red font-medium mt-1"
                >{{ entry.review }}</span
              >
              <span v-if="key === 'team'" class="block text-gray-600 mt-1">
                Latest update: {{ formatActivityDate(entry.teamUpdatedAt) }}
              </span>
              <span v-if="key == 'name'" class="block font-normal">{{
                entry["email"]
              }}</span>
            </span>
            <span v-if="key == 'active'" class="block">
              <span
                :class="{ 'btn-outline-danger': !entry[key] }"
                class="btn btn-outline-success w-24"
                ><span v-if="!entry[key]">In</span>Active</span
              >
            </span>
            <span v-if="key == 'role'" class="block">
              <span
                :class="{ 'btn-outline-danger': entry[key] === 'admin' }"
                class="btn btn-outline w-24 capitalize"
                >{{ entry[key] }}</span
              >
            </span>
          </td>
          <td class="border-b px-4 py-2">
            <router-link class="btn btn-outline" :to="`/user/${entry['id']}`"
              ><EditIcon class="h-4 w-4 mr-1" /> Edit</router-link
            >
            <button
              v-if="selectable && entry.active !== false"
              type="button"
              class="btn btn-outline-danger mt-2"
              :disabled="actionsDisabled"
              @click="$emit('deactivate', entry.id)"
            >
              Deactivate
            </button>
            <button
              v-if="allowReactivation && entry.active === false"
              type="button"
              class="btn btn-outline-success mt-2"
              :aria-label="`Reactivate ${entry.name}`"
              :disabled="actionsDisabled"
              @click="$emit('reactivate', entry.id)"
            >
              {{ reactivatingId === entry.id ? "Reactivating…" : "Reactivate" }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { defineComponent, reactive, ref, computed } from "vue";
import {
  compareActivityDates,
  formatActivityDate
} from "@/utils/user-activity";

export default defineComponent({
  props: {
    serverSide: Boolean,
    sort: { type: String, default: "" },
    selectable: Boolean,
    selectedIds: { type: Array, default: () => [] },
    allSelected: Boolean,
    actionsDisabled: Boolean,
    allowReactivation: Boolean,
    reactivatingId: { type: String, default: "" },
    gridData: {
      type: Array,
      default: function() {
        return [];
      },
      required: false
    },
    query: {
      type: String,
      default: function() {
        return "";
      },
      required: false
    }
  },
  emits: ["sort", "select", "deactivate", "reactivate"],
  setup(props, { emit }) {
    const localSortKey = ref("");
    const sortKey = computed(() =>
      props.serverSide ? props.sort.split(":")[0] : localSortKey.value
    );
    const columns = [
      "name",
      "role",
      "team",
      "lastLoginAt",
      "teamHistory",
      "active"
    ];
    const dateColumns = ["lastLoginAt"];
    const columnLabels = {
      name: "Name",
      role: "Role",
      team: "Team",
      active: "Account status",
      lastLoginAt: "Last login",
      teamHistory: "Recent team history"
    };
    let sortOrders = reactive({});
    columns.forEach(function(key) {
      sortOrders[key] = 1;
    });

    const filteredData = computed(() => {
      if (props.serverSide) return props.gridData;
      let searchQuery = props.query;
      let data = props.gridData;
      let theSortKey = sortKey.value;
      let theSearchQuery = searchQuery && searchQuery.toLowerCase();
      let order = sortOrders[theSortKey] || 1;
      if (searchQuery) {
        data = props.gridData.filter(function(row) {
          return Object.keys(row).some(function(key) {
            return (
              String(
                dateColumns.includes(key)
                  ? formatActivityDate(row[key])
                  : row[key]
              )
                .toLowerCase()
                .indexOf(theSearchQuery) > -1
            );
          });
        });
      }
      if (theSortKey) {
        data = data.slice().sort(function(a, b) {
          if (dateColumns.includes(theSortKey)) {
            return compareActivityDates(a[theSortKey], b[theSortKey], order);
          }
          a = a[theSortKey];
          b = b[theSortKey];
          return (a === b ? 0 : a > b ? 1 : -1) * order;
        });
      }
      return data;
    });
    const eligibleRows = computed(() =>
      props.gridData.filter(row => row.active !== false)
    );

    const sortBy = key => {
      if (props.serverSide) {
        if (key !== "teamHistory")
          emit("sort", {
            key,
            direction: props.sort === `${key}:asc` ? "desc" : "asc"
          });
        return;
      }
      localSortKey.value = key;
      sortOrders[key] = sortOrders[key] * -1;
    };

    return {
      sortKey,
      sortOrders,
      columns,
      columnLabels,
      dateColumns,
      formatActivityDate,
      filteredData,
      eligibleRows,
      sortBy
    };
  }
});
</script>
<style scoped>
.table-report tr:hover td {
  @apply bg-gray-100;
}

th.active .arrow {
  opacity: 1;
}

.arrow {
  display: inline-block;
  vertical-align: middle;
  width: 0;
  height: 0;
  margin-left: 5px;
  opacity: 0.66;
}

.arrow.asc {
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 4px solid #000;
}

.arrow.dsc {
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #000;
}
</style>
